import { FC, useMemo } from "react";
import classNames from "classnames";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";

import { CopyAddress } from "../copy-address";
import { CopyIcon, EditIcon } from "@/components/assets/icons";
import { TAddressDetailItemProps } from "../types";
import { ShowWhen } from "@/components/common";
import { Country, Theme } from "@/enums";
import { useTheme } from "@/context/Theme.context";

const AddressDetailsItem: FC<TAddressDetailItemProps> = ({
  address,
  editable,
  isLast,
  showSms,
  roomNumber,
  selectedCountryId,
}) => {
  const router = useRouter();
  const t = useTranslations();
  const theme = useTheme();

  const displayedAddressValue = useMemo(() => {
    const showRoomNumber = address.label === "address_line_2";
    const isStringValue = typeof address.value === "string";

    const shouldReplaceRoomNumber = (() => {
      if (!isStringValue) return false;
      if (theme === Theme.UZ || theme === Theme.UK) {
        if (
          selectedCountryId === Country.CHINA_AIR ||
          selectedCountryId === Country.CHINA_LAND
        ) {
          return address.value.includes("RoomNumber");
        }
      }
      if (theme === Theme.GEORGIA) {
        if (
          selectedCountryId === Country.CHINA_AIR ||
          selectedCountryId === Country.CHINA_LAND ||
          selectedCountryId === Country.GREECE ||
          selectedCountryId === Country.TURKEY
        ) {
          return address.value.includes("RoomNumber");
        }
      }
      return address.value.includes("{ROOM}");
    })();

    const resolved = (() => {
      if (!shouldReplaceRoomNumber || !isStringValue) return address.value;

      if (theme === Theme.UZ || theme === Theme.UK) {
        if (address.value.includes("RoomNumber")) {
          return address.value.replace(/\RoomNumber/g, roomNumber);
        }
      }

      if (theme === Theme.GEORGIA) {
        if (address.value.includes("RoomNumber")) {
          return address.value.replace(/RoomNumber/gi, roomNumber);
        }
      }

      return address.value.replace("{ROOM}", roomNumber);
    })();

    if (showRoomNumber) return `${address.value} ${roomNumber}`;

    if (shouldReplaceRoomNumber) return resolved;

    return address.value || "";
  }, [address.value, address.label, roomNumber, selectedCountryId, theme]);

  return (
    <div
      className={classNames("flex items-center justify-between py-3", {
        "border-b border-[#f0f0f0]": isLast === false,
      })}
    >
      <div className="w-1/4">
        <p className="text-left text-base font-semibold text-black">
          {t(address.label)}
        </p>
      </div>
      <div className="flex w-1/3 justify-start">
        <ShowWhen condition={address.label !== "phone"}>
          <p className="text-base font-normal text-black">
            {displayedAddressValue}
          </p>
        </ShowWhen>
        <ShowWhen condition={!!showSms && address.label === "phone"}>
          <div className="flex items-center gap-2">
            <p className="text-base font-normal text-black">{address.value}</p>
            <a
              className="cursor-pointer text-base font-bold text-[#13CFE8]"
              href={
                theme === Theme.UZ
                  ? "https://web.telegram.org/k/#-2046923398"
                  : theme === Theme.GEORGIA
                    ? "https://t.me/+IyaPtSHz5Ko3MmJi"
                    : ""
              }
              target="_blank"
              rel="noreferrer"
            >
              SMS
            </a>
          </div>
        </ShowWhen>
        <ShowWhen condition={!showSms && address.label === "phone"}>
          <p className="text-base font-normal text-black">{address.value}</p>
        </ShowWhen>
      </div>
      <div className="flex w-[60px] items-center justify-end gap-3">
        {!!editable && (
          <EditIcon
            onClick={() => router.push("/profile/personal-information")}
            className="mb-1 cursor-pointer fill-[#ADADAD]"
          />
        )}
        <CopyAddress text={displayedAddressValue}>
          <CopyIcon />
        </CopyAddress>
      </div>
    </div>
  );
};

export { AddressDetailsItem };
